import React, { type KeyboardEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { MODAL_TYPES } from '@components/modals/constants';
import { ConfirmDialog } from '@components/primitives/ConfirmDialog';
import { FeatureToggleCard } from '@components/ui/cards/FeatureToggleCard';
import { cleanCptCodes, CPTCodePicker } from '@components/ui/forms/CPTCodePicker';
import { PlaceOfServiceSelect } from '@features/billing/components/Claim/PlaceOfServiceSelect';
import { useAuditEnabled } from '@features/jotaudit';
import { useBundleCatalog } from '@features/subscriptions';
import { useTelehealthAccess } from '@features/telehealth';
import { ArrowForward, Close, Videocam } from '@mui/icons-material';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks';
import { useSnackbar } from 'notistack';

import TemplatePicker from '../../../components/Pickers/TemplatePicker';
import { type UserTemplateItem } from '../../../types/types';
import { useAvailableColors } from '../hooks';
import {
  useCreateAppointmentType,
  useDeleteAppointmentType,
  useUpdateAppointmentType,
} from '../store/hooks';
import {
  type AppointmentType,
  type CreateAppointmentTypeRequest,
  type UpdateAppointmentTypeRequest,
} from '../types';

import { ColorDot } from './ColorDot';
import ExpandableField, { type ExpandableFieldItem, type SchedulingData } from './ExpandableField';

interface FormScheduleItem extends ExpandableFieldItem {
  form_definition_id: string;
  name: string;
  offset: string;
  timing?: string;
  title?: string;
}

interface ReminderItem extends ExpandableFieldItem {
  reminder_template_id: string;
  name: string;
  offset: string;
  timing?: string;
}

interface AppointmentTypeModalProps {
  open: boolean;
  onClose: () => void;
  onSave?: (appointmentType: AppointmentType) => void;
  type: 'create' | 'edit';
  // For create mode
  initialName?: string;
  defaultValues?: {
    basedOnAppointmentType?: AppointmentType;
    overrides?: {
      duration?: number;
      templateId?: string;
      telehealth?: boolean;
      forms?: ExpandableFieldItem[];
      reminders?: ExpandableFieldItem[];
    };
  };
  // For edit mode
  appointmentType?: AppointmentType | null;
  // For duplicate name validation — passed from parent to avoid extra subscription
  existingAppointmentTypes?: AppointmentType[];
}

const APPOINTMENT_REMINDER = {
  id: 'appointment-reminder',
  reminder_template_id: 'xxx',
  name: 'Appointment Reminder',
};

const AppointmentTypeModal: React.FC<AppointmentTypeModalProps> = ({
  open,
  onClose,
  onSave,
  type,
  initialName = '',
  defaultValues,
  appointmentType,
  existingAppointmentTypes = [],
}) => {
  const { createNewAppointmentType, isCreating } = useCreateAppointmentType();
  const { updateAppointmentType, isUpdating } = useUpdateAppointmentType();
  const { deleteAppointmentType, isDeleting } = useDeleteAppointmentType();
  const availableColors = useAvailableColors();
  const { appointment, error: trackError } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const isAuditEnabled = useAuditEnabled();
  const { hasAccess: hasTelehealthAccess } = useTelehealthAccess();
  const { open: openModal } = useModal();

  // Lookup telehealth addon intro offer for dynamic badge/description
  const { addOns } = useBundleCatalog();
  const telehealthIntroOffer = useMemo(() => {
    const addon = addOns.find(b =>
      b.grants?.some(g => g.capability_name === 'telehealth'),
    );
    return addon?.intro_offer ?? null;
  }, [addOns]);

  const isLoading = type === 'create' ? isCreating : isUpdating;

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [enterKeyReleased, setEnterKeyReleased] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    color: 'color1' as string,
    defaultTemplateId: null as string | null,
    defaultTemplate: null as UserTemplateItem | null,
    defaultDuration: null as number | null,
    cptCodes: ['', ''] as string[],
    telehealth: false,
    defaultModality: null as 'in_person' | 'video' | 'audio' | null,
    defaultPlaceOfService: null as string | null,
    selectedForms: [] as FormScheduleItem[],
    selectedReminders: [] as ReminderItem[],
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);
  const [initialColor, setInitialColor] = useState<string | null>(null);

  // Track modal opened
  useEffect(() => {
    if (open) {
      appointment.typeModalOpened({
        mode: type,
        appointment_type_id: type === 'edit' ? appointmentType?.encounter_type_id : undefined,
      });
    }
  }, [open, type, appointmentType?.encounter_type_id, appointment]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (type === 'create') {
        // Extract values with overrides taking precedence
        const baseType = defaultValues?.basedOnAppointmentType;
        const overrides = defaultValues?.overrides;

        const templateId = overrides?.templateId ?? baseType?.template_id ?? null;
        const duration = overrides?.duration ?? baseType?.default_duration ?? null;
        const telehealth = overrides?.telehealth ?? baseType?.telehealth ?? false;
        const color = baseType?.color || 'color1';

        // Handle CPT codes
        const baseCptCodes = baseType?.default_cpt_codes || [];
        const cptCodes = [baseCptCodes[0] || '', baseCptCodes[1] || ''];

        // For forms and reminders, use overrides if provided, otherwise extract from baseType
        const forms =
          overrides?.forms ??
          (baseType?.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) ||
            []);

        const reminders =
          overrides?.reminders ??
          (baseType?.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) ||
            []);

        setFormData({
          name: initialName,
          color: color,
          defaultTemplateId: templateId,
          defaultTemplate: null,
          defaultDuration: duration,
          cptCodes: cptCodes,
          telehealth: telehealth,
          defaultModality: baseType?.default_modality ?? null,
          defaultPlaceOfService: baseType?.default_place_of_service ?? null,
          selectedForms: forms.map(
            form =>
              ({
                ...form,
                form_definition_id: (form as any).form_definition_id || form.formId || '',
                offset: form.offset || '',
              }) as FormScheduleItem,
          ),
          selectedReminders: reminders.map(
            reminder =>
              ({
                ...reminder,
                reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
                offset: reminder.offset || '',
              }) as ReminderItem,
          ),
        });
        setInitialColor(color);
      } else if (type === 'edit' && appointmentType) {
        // Edit mode - populate from existing appointment type
        const forms =
          appointmentType.default_form_rules?.map((rule, index) => ({
            id: `form-${index}`,
            name: rule.title || rule.form_definition_id || `Form ${index + 1}`,
            form_definition_id: rule.form_definition_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            formId: rule.form_definition_id,
            status: 'configured' as const,
          })) || [];

        const reminders =
          appointmentType.default_reminder_rules?.map((rule, index) => ({
            id: `reminder-${index}`,
            name: rule.title || rule.reminder_template_id || `Reminder ${index + 1}`,
            reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
            offset: rule.offset || '',
            timing: rule.offset || '',
            status: 'configured' as const,
          })) || [];

        const baseCptCodes = appointmentType.default_cpt_codes || [];
        const cptCodes = [baseCptCodes[0] || '', baseCptCodes[1] || ''];

        const currentColor = appointmentType.color || 'color1';
        setFormData({
          name: appointmentType.encounter_type,
          color: currentColor,
          defaultTemplateId: appointmentType.template_id || null,
          defaultTemplate: null,
          defaultDuration: appointmentType.default_duration || null,
          cptCodes: cptCodes,
          telehealth: appointmentType.telehealth || false,
          defaultModality: appointmentType.default_modality ?? null,
          defaultPlaceOfService: appointmentType.default_place_of_service ?? null,
          selectedForms: forms,
          selectedReminders: reminders,
        });
        setInitialColor(currentColor);
      }
      setErrors({});
      setSaveError(null);
    }
  }, [open, type, initialName, defaultValues, appointmentType]);

  const handleSave = async () => {
    // Validate required fields
    const newErrors: Record<string, string> = {};
    setSaveError(null);

    if (!formData.name.trim()) {
      newErrors.name = 'Appointment type name is required';
    } else {
      // Check for duplicate name (case-insensitive), excluding current type when editing
      const trimmedName = formData.name.trim().toLowerCase();
      const isDuplicate = existingAppointmentTypes.some(
        (at) =>
          at.encounter_type.toLowerCase() === trimmedName &&
          (type !== 'edit' || at.encounter_type_id !== appointmentType?.encounter_type_id),
      );
      if (isDuplicate) {
        newErrors.name = `An appointment type named "${formData.name.trim()}" already exists`;
      }
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    if (type === 'create') {
      // Create mode
      const appointmentTypeData: CreateAppointmentTypeRequest & {
        default_modality?: 'in_person' | 'video' | 'audio';
        default_place_of_service?: string;
      } = {
        encounter_type: formData.name,
        color: formData.color,
        default_duration: formData.defaultDuration || undefined,
        ...(isAuditEnabled && {
          default_cpt_codes: cleanCptCodes(formData.cptCodes),
        }),
        telehealth: formData.telehealth,
        default_modality: formData.defaultModality || undefined,
        default_place_of_service: formData.defaultPlaceOfService || undefined,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
      };

      try {
        const result = await createNewAppointmentType(appointmentTypeData);
        // Track successful creation
        appointment.typeCreated({
          appointment_type_id: result.encounter_type_id,
          color_selected: formData.color,
          has_default_duration: !!formData.defaultDuration,
          has_default_template: !!formData.defaultTemplateId,
          has_default_forms: formData.selectedForms.length > 0,
          has_default_reminders: formData.selectedReminders.length > 0,
          has_default_cpt_codes: formData.cptCodes.some(code => code.trim() !== ''),
          is_telehealth: formData.telehealth,
        });

        enqueueSnackbar('Appointment type created successfully', { variant: 'success' });

        if (onSave) {
          onSave(result);
        }
        onClose();
      } catch (e: any) {
        const apiMessage = e?.data?.message || e?.data?.error || '';
        if (apiMessage.toLowerCase().includes('already exists')) {
          setErrors({ name: `An appointment type named "${formData.name}" already exists` });
        } else {
          setSaveError('Failed to create appointment type. Please try again.');
          trackError.track(e as Error, 'appointment_type_creation', {
            color_selected: formData.color,
            has_default_duration: String(!!formData.defaultDuration),
          });
        }
      }
    } else if (type === 'edit' && appointmentType) {
      // Edit mode
      const nameChanged = formData.name !== appointmentType.encounter_type;
      const updateData: UpdateAppointmentTypeRequest & {
        default_modality?: 'in_person' | 'video' | 'audio';
        default_place_of_service?: string;
      } = {
        ...(nameChanged && { encounter_type: formData.name }),
        color: formData.color,
        default_duration: formData.defaultDuration || undefined,
        ...(isAuditEnabled && {
          default_cpt_codes: cleanCptCodes(formData.cptCodes),
        }),
        telehealth: formData.telehealth,
        default_modality: formData.defaultModality || undefined,
        default_place_of_service: formData.defaultPlaceOfService || undefined,
        template_id: formData.defaultTemplateId,
        default_form_rules: formData.selectedForms.map(form => ({
          form_definition_id: form.form_definition_id,
          offset: form.offset,
        })),
        default_reminder_rules: formData.selectedReminders.map(reminder => ({
          reminder_template_id: reminder.reminder_template_id,
          offset: reminder.offset,
        })),
      };

      // Determine what fields changed
      const fieldsChanged: string[] = [];
      if (formData.name !== appointmentType.encounter_type) fieldsChanged.push('name');
      if (formData.color !== initialColor) fieldsChanged.push('color');
      if (formData.defaultDuration !== appointmentType.default_duration)
        fieldsChanged.push('duration');
      if (
        JSON.stringify(formData.cptCodes.filter(c => c)) !==
        JSON.stringify(appointmentType.default_cpt_codes || [])
      )
        fieldsChanged.push('cpt_codes');
      if (formData.telehealth !== appointmentType.telehealth) fieldsChanged.push('telehealth');
      if (formData.defaultTemplateId !== appointmentType.template_id)
        fieldsChanged.push('template');
      if (
        JSON.stringify(formData.selectedForms) !==
        JSON.stringify(appointmentType.default_form_rules)
      )
        fieldsChanged.push('forms');
      if (
        JSON.stringify(formData.selectedReminders) !==
        JSON.stringify(appointmentType.default_reminder_rules)
      )
        fieldsChanged.push('reminders');

      try {
        const result = await updateAppointmentType(appointmentType.encounter_type_id, updateData);
        // Track successful update
        appointment.typeUpdated({
          appointment_type_id: appointmentType.encounter_type_id,
          color_changed: formData.color !== initialColor,
          color_selected: formData.color,
          fields_changed: fieldsChanged,
        });

        enqueueSnackbar('Appointment type updated successfully', { variant: 'success' });

        if (onSave) {
          onSave(result);
        }
        onClose();
      } catch (e: any) {
        const apiMessage = e?.data?.message || e?.data?.error || '';
        if (apiMessage.toLowerCase().includes('already exists')) {
          setErrors({ name: `An appointment type named "${formData.name}" already exists` });
        } else {
          setSaveError('Failed to update appointment type. Please try again.');
          trackError.track(e as Error, 'appointment_type_update', {
            appointment_type_id: appointmentType.encounter_type_id,
            fields_changed: fieldsChanged.join(','),
          });
        }
      }
    }
  };

  const handleDeleteClick = () => {
    if (appointmentType) {
      appointment.typeDeleteClicked({
        appointment_type_id: appointmentType.encounter_type_id,
      });
      setShowDeleteConfirm(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!appointmentType) return;

    try {
      const success = await deleteAppointmentType(appointmentType.encounter_type_id);
      if (success) {
        appointment.typeDeleted({
          appointment_type_id: appointmentType.encounter_type_id,
        });
        enqueueSnackbar('Appointment type deleted successfully', { variant: 'success' });
        setShowDeleteConfirm(false);
        onClose();
      }
    } catch (e) {
      trackError.track(e as Error, 'appointment_type_delete', {
        appointment_type_id: appointmentType.encounter_type_id,
      });
      enqueueSnackbar('Failed to delete appointment type', { variant: 'error' });
    }
  };

  // Handle template selection
  const handleTemplateSelect = (template: UserTemplateItem | undefined) => {
    setFormData(prev => ({
      ...prev,
      defaultTemplateId: template?.template_id || null,
      defaultTemplate: template || null,
    }));
  };

  const handleTelehealthToggle = useCallback((checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      telehealth: checked,
      ...(checked && { defaultModality: 'video' as const }),
    }));
  }, []);

  const handleModalityChange = useCallback((value: string) => {
    const modality = (value as 'in_person' | 'video' | 'audio') || null;
    setFormData(prev => ({
      ...prev,
      defaultModality: modality,
      ...(modality === 'video' && { telehealth: true }),
      ...((modality === 'in_person' || modality === 'audio') && { telehealth: false }),
    }));
  }, []);

  const handleAddForm = (schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, true, schedulingData.customTiming);

    const newForm: FormScheduleItem = {
      id: Date.now().toString(),
      form_definition_id: schedulingData.formId || schedulingData.name,
      name: schedulingData.name,
      title: schedulingData.name,
      offset: offset,
      timing: schedulingData.timing,
    };

    setFormData(prev => ({
      ...prev,
      selectedForms: [...prev.selectedForms, newForm],
    }));
  };

  const handleUpdateForm = (formId: string | number, schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, true, schedulingData.customTiming);

    setFormData(prev => ({
      ...prev,
      selectedForms: prev.selectedForms.map(form =>
        form.id === formId
          ? {
              ...form,
              form_definition_id: schedulingData.formId || schedulingData.name,
              name: schedulingData.name,
              title: schedulingData.name,
              offset: offset,
              timing: schedulingData.timing,
            }
          : form,
      ),
    }));
  };

  const handleRemoveForm = (formId: string | number) => {
    setFormData(prev => ({
      ...prev,
      selectedForms: prev.selectedForms.filter(form => form.id !== formId),
    }));
  };

  const handleAddReminder = (schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, false, schedulingData.customTiming);

    const newReminder: ReminderItem = {
      id: Date.now().toString(),
      reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
      name: APPOINTMENT_REMINDER.name,
      offset: offset,
      timing: schedulingData.timing,
    };

    setFormData(prev => ({
      ...prev,
      selectedReminders: [...prev.selectedReminders, newReminder],
    }));
  };

  const handleUpdateReminder = (reminderId: string | number, schedulingData: SchedulingData) => {
    const offset = convertTimingToOffset(schedulingData.timing, false, schedulingData.customTiming);

    setFormData(prev => ({
      ...prev,
      selectedReminders: prev.selectedReminders.map(reminder =>
        reminder.id === reminderId
          ? {
              ...reminder,
              reminder_template_id: APPOINTMENT_REMINDER.reminder_template_id,
              name: APPOINTMENT_REMINDER.name,
              offset: offset,
              timing: schedulingData.timing,
            }
          : reminder,
      ),
    }));
  };

  const handleRemoveReminder = (reminderId: string | number) => {
    setFormData(prev => ({
      ...prev,
      selectedReminders: prev.selectedReminders.filter(reminder => reminder.id !== reminderId),
    }));
  };

  // Helper function to convert timing strings to ISO 8601 duration format
  const convertTimingToOffset = (
    timing: string,
    isForm: boolean,
    customTiming?: string,
  ): string => {
    if (customTiming) {
      return customTiming;
    }

    if (isForm) {
      switch (timing) {
        case 'Immediately before appointment':
          return 'PT0H';
        case '1 hour before appointment':
          return '-PT1H';
        case '1 day before appointment':
          return '-P1D';
        case '2 days before appointment':
          return '-P2D';
        case '1 week before appointment':
          return '-P7D';
        case 'Immediately after appointment':
          return 'PT0H';
        case '1 hour after appointment':
          return 'PT1H';
        case '1 day after appointment':
          return 'P1D';
        default:
          return timing;
      }
    } else {
      switch (timing) {
        case '1 hour before appointment':
          return '-PT1H';
        case '2 hours before appointment':
          return '-PT2H';
        case '1 day before appointment':
          return '-P1D';
        case '2 days before appointment':
          return '-P2D';
        case '1 week before appointment':
          return '-P7D';
        default:
          return timing;
      }
    }
  };

  // Handle Enter key press and release to submit form
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      if (
        enterKeyReleased &&
        !event.shiftKey &&
        !event.ctrlKey &&
        formData.name.trim() &&
        !isLoading
      ) {
        event.preventDefault();
        handleSave();
      }
      setEnterKeyReleased(false);
    }
  };

  const handleKeyUp = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      setEnterKeyReleased(true);
    }
  };

  // Don't render if edit mode but no appointment type
  if (type === 'edit' && !appointmentType) {
    return null;
  }

  const dialogTitle = type === 'create' ? 'Create new Appointment Type' : 'Edit Appointment Type';
  const submitButtonText =
    type === 'create' ? 'Create Appointment Type' : 'Update Appointment Type';
  const loadingText = type === 'create' ? 'Creating...' : 'Updating...';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: '90vh',
        },
      }}
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {dialogTitle}
        <IconButton onClick={onClose} aria-label="Close" edge="end" size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Error Display */}
          {saveError && (
            <Alert severity="error">{saveError}</Alert>
          )}

          {/* Encounter Type Name and Color */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <TextField
              label="Name (e.g. Intake 60 min)"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ flex: 1, minWidth: '200px' }}
              fullWidth
              autoFocus
              required
            />

            <FormControl sx={{ minWidth: 80 }}>
              <InputLabel>Color</InputLabel>
              <Select
                value={formData.color}
                onChange={e => {
                  const newColor = e.target.value;
                  const previousColor = formData.color;
                  setFormData(prev => ({ ...prev, color: newColor }));

                  // Track color change
                  appointment.typeColorChanged({
                    appointment_type_id:
                      type === 'edit' ? appointmentType?.encounter_type_id : undefined,
                    previous_color: previousColor,
                    new_color: newColor,
                    context: type,
                  });
                }}
                label="Color"
                renderValue={value => (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                    <ColorDot colorEnum={value} size={20} />
                  </Box>
                )}
              >
                {availableColors.map(option => (
                  <MenuItem key={option.value} value={option.value} aria-label={option.label}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                      <ColorDot colorEnum={option.value} size={20} />
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Default Template + Duration */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'flex-start',
              flexWrap: { xs: 'wrap', sm: 'nowrap' },
            }}
          >
            <Box sx={{ flex: 1, minWidth: '200px' }}>
              <TemplatePicker
                initialTemplateId={formData.defaultTemplateId}
                onTemplateSelect={handleTemplateSelect}
                required={false}
                label="Default Note Template"
              />
            </Box>
            <TextField
              label="Default Duration (minutes)"
              type="number"
              value={formData.defaultDuration || ''}
              onChange={e => {
                const value = parseInt(e.target.value, 10);
                setFormData(prev => ({
                  ...prev,
                  defaultDuration: isNaN(value) || value <= 0 ? null : value,
                }));
              }}
              inputProps={{
                min: 1,
                max: 480,
                step: 5,
              }}
              helperText="Enter duration in minutes"
              sx={{ minWidth: '180px' }}
            />
          </Box>

          {/* JotMeet */}
          {hasTelehealthAccess ? (
            <FeatureToggleCard
              icon={<Videocam />}
              title="JotMeet"
              description={
                formData.telehealth
                  ? 'A video room will be created for appointments of this type.'
                  : 'Enable to include video visits for this appointment type.'
              }
              checked={formData.telehealth}
              onChange={handleTelehealthToggle}
              switchAriaLabel="Toggle JotMeet for this appointment type"
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: { xs: 'flex-start', md: 'center' },
                flexDirection: 'column',
                '@media (min-width: 768px)': {
                  flexDirection: 'row',
                },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.telehealth}
                    onChange={e => handleTelehealthToggle(e.target.checked)}
                  />
                }
                label="Make telehealth the default"
                sx={{ flexShrink: 0 }}
              />
              <FeatureToggleCard
                icon={<Videocam />}
                title="JotMeet"
                description={
                  telehealthIntroOffer?.percent_off === 100
                    ? 'Video visits with automatic AI notes. Try free for 1 month.'
                    : 'Video visits with automatic AI notes.'
                }
                badge={
                  telehealthIntroOffer?.percent_off === 100
                    ? <Chip label="First month free" size="small" color="primary" sx={{ height: 20, fontSize: '0.7rem' }} />
                    : undefined
                }
                action={
                  <Link
                    component="button"
                    underline="hover"
                    onClick={() => openModal(MODAL_TYPES.ADDON_ONBOARDING, { addonId: 'jotmeet', sx: { borderRadius: 3, overflow: 'hidden' } })}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Learn more <ArrowForward sx={{ fontSize: 14 }} />
                  </Link>
                }
              />
            </Box>
          )}

          {/* Billing Defaults */}
          <Box
            component="fieldset"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              m: 0,
            }}
          >
            <legend>
              <Typography variant="subtitle2" sx={{ px: 0.5 }}>
                Billing Defaults
              </Typography>
            </legend>
            <Stack spacing={2}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                }}
              >
                <FormControl sx={{ flex: 1, minWidth: '200px' }}>
                  <InputLabel>Default Modality</InputLabel>
                  <Select
                    value={formData.defaultModality || ''}
                    onChange={e => handleModalityChange(e.target.value)}
                    label="Default Modality"
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="in_person">In-person</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                    <MenuItem value="audio">Audio</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ flex: 1, minWidth: '200px' }}>
                  <PlaceOfServiceSelect
                    value={formData.defaultPlaceOfService || ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        defaultPlaceOfService: e.target.value || null,
                      }))
                    }
                    label="Default Place of Service"
                    size="medium"
                    fullWidth
                  />
                </Box>
              </Box>

              {isAuditEnabled && (
                <CPTCodePicker
                  value={formData.cptCodes}
                  onChange={codes => setFormData(prev => ({ ...prev, cptCodes: codes }))}
                  showHeader={false}
                  showHelperText={false}
                  size="medium"
                />
              )}

              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                This information will help JotPsych audit your note to ensure reimbursement.
              </Typography>
            </Stack>
          </Box>

          {/* Patient Workflow */}
          <Box
            component="fieldset"
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              m: 0,
            }}
          >
            <legend>
              <Typography variant="subtitle2" sx={{ px: 0.5 }}>
                Patient Workflow
              </Typography>
            </legend>
            <Stack spacing={2}>
              <ExpandableField
                title="Attach Default Form(s)"
                items={formData.selectedForms}
                onAddItem={handleAddForm}
                onUpdateItem={handleUpdateForm}
                onDeleteItem={handleRemoveForm}
                isFormType={true}
                mode={type}
              />
              <ExpandableField
                title="Default Reminders"
                items={formData.selectedReminders}
                onAddItem={handleAddReminder}
                onUpdateItem={handleUpdateReminder}
                onDeleteItem={handleRemoveReminder}
                isFormType={false}
                mode={type}
              />
            </Stack>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'space-between' }}>
        <Box>
          {type === 'edit' && (
            <Button color="error" onClick={handleDeleteClick} disabled={isLoading || isDeleting}>
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} color="inherit" disabled={isLoading}>
            Cancel
          </Button>
          <Tooltip
            title={!formData.name.trim() ? 'Enter appointment type name' : ''}
            placement="top"
          >
            <span>
              <Box sx={{ position: 'relative' }}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={!formData.name.trim() || isLoading}
                  sx={{
                    color: 'white',
                    paddingRight: formData.name.trim() && !isLoading ? '30px' : '16px',
                  }}
                  startIcon={isLoading ? <CircularProgress size={16} /> : null}
                >
                  {isLoading ? loadingText : submitButtonText}
                </Button>
                {formData.name.trim() && !isLoading && (
                  <KeyboardReturnIcon
                    fontSize="small"
                    sx={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '1rem',
                      opacity: 0.7,
                      color: 'white',
                    }}
                  />
                )}
              </Box>
            </span>
          </Tooltip>
        </Box>
      </DialogActions>

      <ConfirmDialog
        open={showDeleteConfirm}
        variant="warning"
        title="Delete Appointment Type?"
        message={`Are you sure you want to delete "${appointmentType?.encounter_type || ''}"? This action cannot be undone.`}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDeleteConfirm}
        confirmText="Delete"
        loading={isDeleting}
      />
    </Dialog>
  );
};

export default AppointmentTypeModal;
